import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

/**
 * Shared "alternating row shading" (zebra striping) preference for Flow tables.
 *
 * Lives in a tiny module-level store (not FlowTable-local state) because the
 * TOGGLE renders in the shell next to the theme control, while the CONSUMER is every FlowTable — they must share one
 * reactive value (Spencer, 2026-07-31: "move the stripes next to the dark/light
 * icon"). Persisted to localStorage so the choice sticks across reloads and is
 * shared across all Flow tables + browser tabs.
 *
 * OFF by default (single color). The toggle is only shown on the Command Center
 * tab, but the preference itself is global — any FlowTable reads it.
 */
const KEY = 'flow-table-zebra-store';

export interface ZebraState {
  zebraStripes: boolean;
}

export const initialState: ZebraState = {
  zebraStripes: false,
};

export const useZebraStripesStore = create<ZebraState>()(
  persist(
    () => ({
      ...initialState,
    }),
    {
      name: KEY,
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

export function setZebraStripes(next: boolean): void {
  useZebraStripesStore.setState({ zebraStripes: next });
}

/** Reactive read of the shared zebra-striping preference. */
export function useZebraStripes(): boolean {
  return useZebraStripesStore((s) => s.zebraStripes);
}

// Cross-tab sync: zustand's `persist` middleware does not sync across tabs by
// default (only the tab that writes updates its own in-memory state). Wire a
// single module-scope `storage` listener (official zustand docs pattern:
// https://zustand.docs.pmnd.rs/integrations/persisting-store-data#how-can-i-rehydrate-on-storage-event)
// that rehydrates from localStorage whenever another tab writes to our key.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e: StorageEvent) => {
    if (e.key === KEY) {
      void useZebraStripesStore.persist.rehydrate();
    }
  });
}
